import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, inject, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { ServiceWorkerModule } from '@angular/service-worker';
import {
	HTTP_INTERCEPTORS,
	HttpClient,
	HttpHandler,
	provideHttpClient,
	withInterceptorsFromDi,
} from '@angular/common/http';
import { IonicStorageModule } from '@ionic/storage-angular';
import { AppRoutingModule } from './app-routing.module';
import { Router } from '@angular/router';
import { AlertController, provideIonicAngular, LoadingController, Platform, IonContent, IonLabel, IonTitle,
	IonToolbar, IonHeader, IonRouterOutlet, IonApp
} from '@ionic/angular/standalone';
import { Drivers } from '@ionic/storage';
import { AppComponent } from './app.component';
import { environment } from '../environments/environment';
import { NgxMaskModule } from 'ngx-mask';

import { LoadingService } from './services/loading.service';
import { ToastService } from './services/toast.service';
import { ValidationHelper } from './helpers/validation.helper';

import { registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import localeItExtra from '@angular/common/locales/extra/it';
import { InterceptorService } from './services/interceptor.service';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { ErrorManagerService } from './services/error-manager.service';
import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation/ngx';
import { FirebaseMessaging } from '@awesome-cordova-plugins/firebase-messaging/ngx';
import { QRCodeModule } from 'angularx-qrcode';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { StorageKeys, StorageService } from './services/storage.service';
import { UsersService } from './services/users.service';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { HTTP } from '@awesome-cordova-plugins/http/ngx';
import { AppService } from './services/app.service';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { InputLabelModule } from './components/input-label/input-label.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ComboBoxModule } from '@syncfusion/ej2-angular-dropdowns';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatListModule } from '@angular/material/list';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AppCoreRootComponent } from './app-core/app-core-root/app-core-root.component';
import { Utility } from './helpers/Utility';
import { assertNotNullOrUndefined } from './app-core/_core/common/guards';
import { ContextService } from './services/context.service';
import { firstValueFrom, timeout } from 'rxjs';
import { EnvironmentService } from './services/environment.service';
import { MenusService } from './services/menus.service';
import { AccountsService } from './web-api/accounts/accounts.service';
import { FeatureFlags } from '../domain/feature-flags';
import { UserProfileInfo } from './models/user-profile.model';
import { EnvironmentEnum } from '../domain/environment.enum';
//import { IonicSelectableModule } from 'ionic-selectable';

registerLocaleData(localeIt, 'it', localeItExtra);

function initDomain() {
	let domainEnv: EnvironmentEnum;
	switch (environment.environmentName) {
		case 'dev':
			domainEnv = EnvironmentEnum.Development;
			break;
		case 'alpha':
			domainEnv = EnvironmentEnum.Alpha;
			break;
		case 'test':
			domainEnv = EnvironmentEnum.Testing;
			break;
		case 'staging':
			domainEnv = EnvironmentEnum.Staging;
			break;
		case 'beta':
			domainEnv = EnvironmentEnum.Beta;
			break;
		default:
			domainEnv = EnvironmentEnum.Production;
			break;
	}
	
	FeatureFlags.init(domainEnv);
}

export function initializeApp() {
	EnvironmentService.init(environment);
	initDomain();
	
	const storageService = inject(StorageService);
	const usersService = inject(UsersService);
	const accountsService = inject(AccountsService);
	const contextService = inject(ContextService);
	const translate = inject(TranslateService);
	const environmentService = inject(EnvironmentService);
	const menusService = inject(MenusService);
	
	const featureFlags = FeatureFlags.instance;
	
	
	return async (): Promise<void> => {
		// await new Promise(resolve => setTimeout(resolve, 1000 * 5))
		await storageService.init();
		await usersService.checkCurrentUser();
		let userToken: string | null = await storageService.get(StorageKeys.user);
		if (!userToken)
			userToken = sessionStorage.getItem(StorageKeys.user);
		if (userToken) {
			try {
				let user: UserProfileInfo;
				const res = await accountsService.getCurrentAccountAsync();
				if(res.currentClient){
					user = UserProfileInfo.fromClientAccount(res.user, res.currentClient.account);
				}
				else if(res.currentCollaborator){
					user = UserProfileInfo.fromCollaboratorAccount(res.user, res.currentCollaborator.account);
				}
				else if(res.currentAdmin){
					user = UserProfileInfo.fromAdminAccount(res.user, res.currentAdmin.account);
				}
				else if(res.currentConsultant){
					user = UserProfileInfo.fromAdminAccount(res.user, res.currentConsultant.account);
				}
				else{
					throw new Error('Auth type not implemented');
				}
				
				usersService.setCurrentUserInfo(user);
				
				if(environmentService.isMenuEnabled() && !res.currentAdmin){
					try {
						menusService.currentMenu = await firstValueFrom(menusService.getCurrentMenu());
						usersService.setLanguages(await firstValueFrom(usersService.getLanguages().pipe(timeout(10000))));
					}
					catch (error){
						console.error("Error during Menu initialization");
						console.error(error);
					}
				}
				
				let id = user.LinguaDefaultApp;
				const nameLang = Utility.languages.find((e) => e.Id === id);
				assertNotNullOrUndefined(nameLang);
				translate.setDefaultLang(nameLang.Nome.toLowerCase());
				translate.use(nameLang.Nome.toLowerCase());
				const permissionCodes = usersService.getPermissionCodes();
				
				const context = usersService.createContext(user, permissionCodes);
				contextService.setContext(context);
			} catch (error) {
				console.error(error);
				await usersService.logout();
			}
			
			return;
		}
		
		translate.use('it');
	}
}

// export function startupStorage(storageService: StorageService): () => void {
// 	return () => storageService.init();
// }

export function HttpLoaderFactory(http: HttpClient) {
	return new TranslateHttpLoader(http);
}

@NgModule({
	declarations: [AppComponent],
	imports: [
		NgxMaskModule.forRoot(),
		BrowserModule,
		BrowserAnimationsModule,
		// IonicModule,
		// IonicModule.forRoot(),
		IonicStorageModule.forRoot({
			name: '_salonappDB',
			driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
		}),
		//IonicSelectableModule,
		AppRoutingModule,
		ServiceWorkerModule.register('ngsw-worker.js', {
			enabled: environment.production,
			registrationStrategy: 'registerWithDelay:7000',
		}),
		NgxGoogleAnalyticsModule.forRoot(environment.googleAnalyticsKey),
		NgxGoogleAnalyticsRouterModule,
		QRCodeModule,
		TranslateModule,
		TranslateModule.forRoot({
			defaultLanguage: 'en',
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
				deps: [HttpClient, HttpHandler],
			},
		}),
		// NgChartsModule,
		InputLabelModule,
		FormsModule,
		ComboBoxModule,
		MatDatepickerModule,
		MatListModule,
		MatFormFieldModule,
		ReactiveFormsModule,
		AppCoreRootComponent,
		IonContent,
		IonLabel,
		IonTitle,
		IonToolbar,
		IonHeader,
		IonRouterOutlet,
		IonApp,
	],
	providers: [
		provideIonicAngular({
			mode: 'md' // Forces Material Design mode globally
		}),
		HTTP,
		LoadingService,
		ToastService,
		StatusBar,
		FirebaseMessaging,
		ScreenOrientation,
		ValidationHelper,
		{ provide: LOCALE_ID, useValue: 'it' },
		// {provide: RouteReuseStrategy, useClass: IonicRouteStrategy},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: InterceptorService,
			multi: true,
			deps: [
				ToastService,
				LoadingController,
				Router,
				Platform,
				AlertController,
				StorageService,
				UsersService,
				AppService,
				// TranslateService,
			],
		},
		{ provide: ErrorHandler, useClass: ErrorManagerService },
		{
			provide: APP_INITIALIZER,
			useFactory: initializeApp,
			deps: [StorageService, ContextService, UsersService, TranslateService],
			multi: true,
		},
		provideHttpClient(withInterceptorsFromDi()),
	],
	bootstrap: [AppComponent]
})
export class AppModule {}
